'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import React from 'react';

import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';

import { AddToCartButton } from '@eliadmin/core/src/components/product/add-to-cart-button';
import { AddToFavoritesButton } from '@eliadmin/core/src/components/product/add-to-favorites-button';

import { DialogProvider } from '@eliadmin/core/src/components/ui/dialog';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { VariantsProvider, useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import {
  getProductFilterVariants,
  getProductImage,
  getProductUrl,
  isSellable,
} from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import { ProductBadge } from '../product-badge';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export type ProductCardProps = {
  product: Product;
  priority?: boolean;
  imageSizes?: string;
  headingTag?: 'h2' | 'h3';
  className?: {
    main?: string;
  };
};

function ProductCardInternal({
  product,
  className,
  priority = false,
  imageSizes,
  headingTag: Title = 'h3',
}: ProductCardProps) {
  const templateSettings = useGlobalStore(s => s.templateSettings);
  const generalLayout = useGlobalStore(s => s.generalLayout);
  const filter = useCategoryStore(state => state.filter);
  const selectedValues = useVariantsStore(state => state.selectedValues);
  const currency = generalLayout.currency;
  const currencySettings = generalLayout.currency_settings;
  const isWholesaler = useCustomerStore(state => state.me?.customer.is_wholesaler);
  const { t } = useTranslations();
  const resolvedImageSizes = imageSizes ?? (priority ? '400px' : 'auto, 400px');

  const filterVariants = getProductFilterVariants(product, filter);
  const image = getProductImage(templateSettings, product, filterVariants);
  const productUrl = getProductUrl(product, selectedValues);

  const priceDisplay = getProductPriceDisplay(product, {
    isWholesaler,
    priceWithoutVatFirst: templateSettings?.product_detail?.price_without_vat_first?.enabled,
  });
  const isDiscounted = priceDisplay.showPreviousPrice;

  const hasTags = product.tags && product.tags.length > 0;
  const priceContent = priceDisplay.priceOnRequest ? (
    <strong className="text-primary">{t('text533')}</strong>
  ) : (
    <>
      {priceDisplay.showMocVocPrices && priceDisplay.mocPrice != null ? (
        <span className="text-xs text-[#6B7280]">
          {t('text1926')} {formatPrice(priceDisplay.mocPrice, currency, currencySettings)}
        </span>
      ) : null}
      <div className="flex w-full items-center justify-start gap-2">
        <strong className={cn('font-bold', isDiscounted ? 'text-[#FF0000]' : 'text-primary')}>
          {formatPrice(priceDisplay.current, currency, currencySettings)}
        </strong>
        {isDiscounted ? (
          <span className="text-[#6B7280] line-through">
            {formatPrice(priceDisplay.beforeDiscount, currency, currencySettings)}
          </span>
        ) : null}
      </div>
      {templateSettings.product_teaser?.price_without_vat?.enabled ? (
        <span className="text-xs text-[#6B7280]">
          {priceDisplay.showMocVocPrices ? `${t('text1925')} ` : ''}
          {formatPrice(priceDisplay.secondary, currency, currencySettings)}{' '}
          {priceDisplay.secondaryIncludesVat ? t('text1122') : t('text443')}
        </span>
      ) : null}
    </>
  );

  return (
    <>
      <div
        className={cn(
          'group',
          'col-span-1 hidden w-full flex-col items-start justify-start sm:flex',
          className?.main,
        )}
      >
        <div className="relative aspect-square w-full overflow-hidden bg-[#F9FAFB]">
            <Link prefetchOnHover
              prefetch={false}
              href={productUrl}
              className="absolute inset-0"
            >
              <div className="absolute left-1/2 top-4 z-10 flex w-full -translate-x-1/2 flex-wrap items-start justify-start gap-1 pl-4 pr-[82px]">
                {!priceDisplay.priceOnRequest && isDiscounted ? (
                  <ProductBadge color="#FF0000" name={`-${priceDisplay.discountLabel}%`} />
                ) : null}
                {hasTags &&
                  product.tags?.map(tag => (
                    <ProductBadge key={tag.name} color={tag.color} name={tag.name} />
                  ))}
                <ProductBadge color={product.availability.color!} name={product.availability.name} />
              </div>
              <Image
                src={image?.url ?? '/images/placeholder.png'}
                alt={resolveImageAlt(image, product?.name)}
                width={0}
                height={0}
                priority={priority}
                fetchPriority={priority ? 'high' : undefined}
                loading={priority ? 'eager' : 'lazy'}
                className="absolute inset-0 z-0 h-full w-full object-contain object-center"
                sizes={resolvedImageSizes}
              />

            </Link>
              <AddToFavoritesButton
                slug={product.slug}
                isFavorite={product.is_favorite ?? false}
                productId={product.id}
                className={{
                  button: cn(
                    'absolute right-4 top-4 z-10 max-h-[50px] min-h-[50px] min-w-[50px] max-w-[50px] rounded-full border-none bg-white hover:bg-white hover:opacity-65',
                  ),
                }}
              />
              <div
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                className={cn(
                  'absolute bottom-4 left-1/2 z-20 w-full -translate-x-1/2 translate-y-[200px] px-4',
                  'group-hover:translate-y-0',
                  'transition-transform duration-200 ease-in-out',
                )}
              >
                <AddToCartButton
                  product={product}
                  showIcon={true}
                  sellable={isSellable(product, 1)}
                  quantity={1}
                  className="relative z-20 w-full"
                />
              </div>
          </div>
          <div className="flex w-full flex-col items-start justify-start gap-2 p-4">
            <Link prefetchOnHover prefetch={false} href={productUrl} className="w-full">
              <Title data-ea-visual-tag="h5" className="line-clamp-2 h-10 text-sm sm:h-12 sm:text-base">{product.name}</Title>
            </Link>
            <Link prefetchOnHover prefetch={false} href={productUrl} className="w-full">
              <div className="flex w-full flex-col items-start gap-1">{priceContent}</div>
            </Link>
          </div>
        </div>
        <div className="col-span-1 flex w-full flex-col items-start justify-start gap-0 sm:hidden">
          <div className="relative aspect-square w-full overflow-hidden bg-[#F9FAFB]">
          <Link prefetchOnHover
            prefetch={false}
            href={productUrl}
            className="absolute inset-0"
          >
            <div className="absolute left-1/2 top-4 z-10 flex w-full -translate-x-1/2 flex-wrap items-start justify-start gap-1 pl-4 pr-[82px]">
              {!priceDisplay.priceOnRequest && isDiscounted ? (
                <ProductBadge color="#FF0000" name={`-${priceDisplay.discountLabel}%`} />
              ) : null}
              {hasTags &&
                product.tags?.map(tag => (
                  <ProductBadge key={tag.name} color={tag.color} name={tag.name} />
                ))}
              <ProductBadge color={product.availability.color!} name={product.availability.name} />
            </div>
            <Image
              src={image?.url ?? '/images/placeholder.png'}
              alt={resolveImageAlt(image, product?.name)}
              width={0}
              height={0}
              className="absolute inset-0 z-0 h-full w-full object-contain object-center"
              sizes={resolvedImageSizes}
              priority={priority}
              fetchPriority={priority ? 'high' : undefined}
              loading={priority ? 'eager' : 'lazy'}
            />
          </Link>
            <AddToFavoritesButton
              slug={product.slug}
              isFavorite={product.is_favorite ?? false}
              productId={product.id}
              className={{
                button: cn(
                  'absolute right-4 top-4 z-10 max-h-[50px] min-h-[50px] min-w-[50px] max-w-[50px] rounded-full border-none bg-white hover:bg-white hover:opacity-65',
                ),
              }}
            />
        </div>
        <div className="flex w-full flex-col items-start justify-start gap-1.5 px-3 py-4">
          <p className="line-clamp-2 h-10 text-sm leading-[1.4] md:h-11 md:text-base md:leading-[1.4]">
            {product.name}
          </p>
          <div className="flex w-full flex-col items-start gap-1 text-sm md:text-base">
            {priceContent}
          </div>
        </div>
        <AddToCartButton
          product={product}
          showIcon={true}
          sellable={isSellable(product, 1)}
          quantity={1}
          className="relative z-20 w-full"
        />
      </div>
    </>
  );
}

export function ProductCard(props: ProductCardProps) {
  const providerKey = props.product.id
    ? `product-${props.product.id}`
    : `product-${props.product.slug}`;

  return (
    <VariantsProvider
      key={providerKey}
      state={{ product: props.product, selectedQuantity: props.product.min_quantity ?? 1 }}
    >
      <DialogProvider>
        <ProductCardInternal {...props} />
      </DialogProvider>
    </VariantsProvider>
  );
}
